import { CampaignStatus } from '@/shared/types/campaigns';

type BadgeFlavour = 'ai' | 'successful' | 'neutral' | 'destructive' | 'informational';

/**
 * Maps a campaign status to its Badge `flavour`. Shared by the campaigns table,
 * the analytics page, and the contact/profile sidebars so the colour coding
 * stays consistent.
 */
export const getCampaignStatusVariant = (status?: CampaignStatus | string): BadgeFlavour => {
  switch (status?.toLowerCase()) {
    case 'in_progress':
      return 'ai';
    case 'sent':
      return 'successful';
    case 'draft':
    case 'canceled':
      return 'neutral';
    case 'failed':
      return 'destructive';
    default:
      return 'informational';
  }
};

/**
 * Maps a campaign status to the Tailwind background class for the small status
 * dot used in the contacts sidebar. Follows the same colour scheme as
 * `getCampaignStatusVariant`, except `in_progress` uses the informational blue
 * (there is no `bg-ai` dot utility).
 */
export const getCampaignStatusDotColor = (status?: CampaignStatus | string): string => {
  switch (status?.toLowerCase()) {
    case 'sent':
      return 'bg-successful';
    case 'draft':
    case 'canceled':
      return 'bg-foreground-muted';
    case 'failed':
      return 'bg-destructive';
    default:
      return 'bg-informational';
  }
};

/**
 * Static i18n keys for the campaign status badge, shared by the table, the
 * analytics title, and the sidebars. A finished campaign (`sent`) reads as
 * "Completed" and a stopped one (`canceled`) reads as "Stopped". All labels live
 * under `campaigns.status.*` so campaigns stay independent of the broadcast
 * module. Keys are static (never concatenated) per the i18n conventions.
 */
const STATUS_LABEL_KEYS: Record<CampaignStatus, string> = {
  draft: 'campaigns.status.draft',
  ready: 'campaigns.status.ready',
  in_progress: 'campaigns.status.in_progress',
  sent: 'campaigns.status.completed',
  failed: 'campaigns.status.failed',
  canceled: 'campaigns.status.stopped',
};

export const getCampaignStatusLabelKey = (status?: CampaignStatus | string): string =>
  (status && STATUS_LABEL_KEYS[status.toLowerCase() as CampaignStatus]) ||
  'campaigns.status.unknown';

/** All campaign statuses in display order (e.g. for the status filter). */
export const CAMPAIGN_STATUSES = Object.keys(STATUS_LABEL_KEYS) as CampaignStatus[];
